import React from 'react';
import Markdown from 'markdown-to-jsx';
import Heading from '@atlaskit/heading';
import CheckIcon from '@atlaskit/icon/glyph/check-circle';
import InfoIcon from '@atlaskit/icon/glyph/editor/info';
import Image from '@atlaskit/image';
import { Box, Stack, Inline, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { TransitionHomeContentBox } from '../../../../common/ui/transition-home/content-box';
import taskMessages from '../../../../common/utils/tasks/messages';
import { useTransitioningStatus } from '../../../../services/store/transitioning-status';
import qrSrc from './assets/qr.png';
import messages from './messages';
import { MobileDeviceList } from './mobile-device-list';

const Layout = ({
	children,
	isRBNEnabled,
	isJnaAppDownloaded,
	ogAppExistsAtTransitioningStart,
}: {
	children: React.ReactNode;
	isRBNEnabled: boolean;
	isJnaAppDownloaded: boolean;
	ogAppExistsAtTransitioningStart: boolean;
}) => {
	const { formatMessage } = useIntl();
	return (
		<TransitionHomeContentBox
			illustration={
				<Stack alignInline="center" space="space.300">
					<Image src={qrSrc} alt="https://atlassian.app.link/jswc-mobile-app-text" />
					<Inline xcss={[inlineStyles, qrTextStyles]}>{formatMessage(messages.downloadJna)}</Inline>
				</Stack>
			}
		>
			<Stack space="space.200" alignInline="start">
				<Heading as="h2" size="small">
					<FormattedMessage {...taskMessages.notificationsTaskTwoNonFinal} />
				</Heading>
				<Box>
					<Markdown>
						{formatMessage(messages.descriptionNonFinal, {
							hideMiddleParagraph:
								isJnaAppDownloaded && !isRBNEnabled && !ogAppExistsAtTransitioningStart,
							isRBNEnabled,
						})}
					</Markdown>
				</Box>
				{children}
			</Stack>
		</TransitionHomeContentBox>
	);
};

export const CardOne = () => {
	const [{ isJnaAppDownloaded, isRBNEnabled, ogAppExistsAtTransitioningStart }] =
		useTransitioningStatus();

	if (
		/* 1A & 1B */ !isJnaAppDownloaded ||
		/* 1G */ (isJnaAppDownloaded && isRBNEnabled) ||
		/* 1F */ (isJnaAppDownloaded && !isRBNEnabled && !ogAppExistsAtTransitioningStart)
	)
		return (
			<Layout {...{ isJnaAppDownloaded, isRBNEnabled, ogAppExistsAtTransitioningStart }}>
				<Inline xcss={inlineStyles} alignBlock="baseline" space="space.050">
					{isJnaAppDownloaded ? (
						<CheckIcon label="" primaryColor={token('color.icon', colors.N500)} size="small" />
					) : (
						<InfoIcon label="" primaryColor={token('color.icon', colors.N500)} size="small" />
					)}

					<FormattedMessage
						{...(isJnaAppDownloaded
							? messages.addedJnaNonFinal
							: messages.couldntDetectJnaNonFinal)}
					/>
				</Inline>
			</Layout>
		);

	// 1E & 1D
	if (isJnaAppDownloaded && !isRBNEnabled && ogAppExistsAtTransitioningStart)
		return (
			<Layout {...{ isJnaAppDownloaded, isRBNEnabled, ogAppExistsAtTransitioningStart }}>
				<MobileDeviceList />
			</Layout>
		);

	throw new Error('CardOne - unexpected state');
};

const inlineStyles = xcss({
	color: 'color.text.subtlest',
});

const qrTextStyles = xcss({
	textAlign: 'center',
	width: '148px',
});
